/**
 * Smart alignment guides for the ad-builder canvas.
 *
 * While an element is dragged, its edges and centre are compared against every
 * other element on the canvas. When an axis comes within SNAP_THRESHOLD the
 * position is nudged onto the match and a guide line is reported so the canvas
 * can draw it, the way Photoshop and Figma do.
 *
 * Pure geometry: no React, no DOM, so the behaviour can be reasoned about (and
 * tested) on its own.
 */

/** How close (in canvas px) an edge must be before it snaps. */
export const SNAP_THRESHOLD = 6;

export interface Box {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Guide {
  /** `v` runs top-to-bottom at a given x; `h` runs left-to-right at a given y. */
  orientation: "v" | "h";
  /** Canvas-space position of the line. */
  position: number;
  /**
   * Extent of the line along its other axis, covering both the dragged element
   * and the element it matched, so the line visibly connects the two rather
   * than spanning the whole canvas.
   */
  start: number;
  end: number;
}

export interface SnapResult {
  x: number;
  y: number;
  guides: Guide[];
}

/** The three candidate positions on each axis: near edge, centre, far edge. */
const axisAnchors = (start: number, size: number) => [
  start,
  start + size / 2,
  start + size,
];

/**
 * Snap a dragged box against its neighbours.
 *
 * Only the closest match on each axis is applied, so a box between two
 * near-equidistant neighbours does not jitter between them.
 */
export function computeSnap(
  dragged: Box,
  others: Box[],
  threshold: number = SNAP_THRESHOLD,
): SnapResult {
  const guides: Guide[] = [];
  let snappedX = dragged.x;
  let snappedY = dragged.y;

  // Track the best (smallest) delta per axis so only one snap wins.
  let bestXDelta = Infinity;
  let bestYDelta = Infinity;
  let bestXGuide: Guide | null = null;
  let bestYGuide: Guide | null = null;

  const draggedXAnchors = axisAnchors(dragged.x, dragged.width);
  const draggedYAnchors = axisAnchors(dragged.y, dragged.height);

  others.forEach((other) => {
    if (other.id === dragged.id) return;

    const otherXAnchors = axisAnchors(other.x, other.width);
    const otherYAnchors = axisAnchors(other.y, other.height);

    // ── Vertical guides (x alignment) ──
    draggedXAnchors.forEach((draggedAnchor, draggedIdx) => {
      otherXAnchors.forEach((otherAnchor) => {
        const delta = otherAnchor - draggedAnchor;
        if (Math.abs(delta) > threshold || Math.abs(delta) >= bestXDelta) return;

        bestXDelta = Math.abs(delta);
        // Shift the box so the matched anchor lands exactly on the guide.
        snappedX = dragged.x + delta;

        // Span the line across both boxes so the relationship is visible.
        const top = Math.min(dragged.y, other.y);
        const bottom = Math.max(
          dragged.y + dragged.height,
          other.y + other.height,
        );
        bestXGuide = {
          orientation: "v",
          position: otherAnchor,
          start: top,
          end: bottom,
        };
        // `draggedIdx` is unused beyond clarity of intent; the delta already
        // encodes which anchor matched.
        void draggedIdx;
      });
    });

    // ── Horizontal guides (y alignment) ──
    draggedYAnchors.forEach((draggedAnchor) => {
      otherYAnchors.forEach((otherAnchor) => {
        const delta = otherAnchor - draggedAnchor;
        if (Math.abs(delta) > threshold || Math.abs(delta) >= bestYDelta) return;

        bestYDelta = Math.abs(delta);
        snappedY = dragged.y + delta;

        const left = Math.min(dragged.x, other.x);
        const right = Math.max(
          dragged.x + dragged.width,
          other.x + other.width,
        );
        bestYGuide = {
          orientation: "h",
          position: otherAnchor,
          start: left,
          end: right,
        };
      });
    });
  });

  if (bestXGuide) guides.push(bestXGuide);
  if (bestYGuide) guides.push(bestYGuide);

  return { x: snappedX, y: snappedY, guides };
}
